import * as React from "react";
import Button from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import { Grid, InputLabel, OutlinedInput, Stack } from "@mui/material";
import { baseURL } from "../../api";
import axios from "axios";
import { setAccessToken } from "../../auth/authClient";
import { toast } from "react-toastify";
import CustomAlert from "../../component/AlertComponent/Alert";
import { useNavigate } from "react-router-dom";
import { Global_Data } from "../../globalData/GlobalData";
import { useEffect, useState } from "react";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2)
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1)
  }
}));

export default function TwoFactorAuthScreen({
  TwoFactorAuthScreenIsOpen,
  setTwoFactorAuthScreenIsOpen,
  twoFactorData,
  setTwoFactorData,
  loginFormData
}){
  const navigate = useNavigate();
  const { setToken, setUserData } = Global_Data?.() || {};
  const [otpValue, setOtpValue] = React.useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(0); 

  const getExpiryTimeInSeconds = (expiryMinutes) => {
    if (!expiryMinutes || expiryMinutes <= 0) return 300;
    return Number(expiryMinutes) * 60; 
  };

  useEffect(() => {
    if (TwoFactorAuthScreenIsOpen && twoFactorData?.["2FA-Expiry"]) {
      const expiryTime = getExpiryTimeInSeconds(twoFactorData["2FA-Expiry"]);
      const savedStartTime = sessionStorage.getItem("twoFactorStartTime");

      if (savedStartTime) {
        const elapsedSeconds = Math.floor((Date.now() - parseInt(savedStartTime, 10)) / 1000);
        const remainingTime = Math.max(0, expiryTime - elapsedSeconds);
        setTimer(remainingTime);
      } else {
        setTimer(expiryTime);
      }
    }
  }, [TwoFactorAuthScreenIsOpen, twoFactorData]);

  useEffect(() => {
    if (timer > 0 && TwoFactorAuthScreenIsOpen) {
      const interval = setInterval(() => {
        setTimer((prevTimer) => {
          if (prevTimer <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prevTimer - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [timer, TwoFactorAuthScreenIsOpen]);

  const handleClose = () => {
    setTwoFactorAuthScreenIsOpen(false);
    setOtpValue("");
    setTimer(0);
    // Clear 2FA session data from sessionStorage
    sessionStorage.removeItem("twoFactorData");
    sessionStorage.removeItem("loginFormData");
    sessionStorage.removeItem("twoFactorAuthScreenIsOpen");
    sessionStorage.removeItem("twoFactorStartTime");
  };

  const findFirstMenuRoute = (dataset) => {
    for (const menu of dataset) {
      if (menu.hasOwnProperty("sMenuRoute") && menu.sMenuRoute && menu.sMenuRoute !== "") {
        return menu.sMenuRoute;
      }
      if (menu.hasOwnProperty("submenus") && Array.isArray(menu.submenus) && menu.submenus.length > 0) {
        const route = findFirstMenuRoute(menu.submenus);
        if (route) return route;
      }
    }
    return "";
  };

  function fetchDrawerRoute(token) {
    return axios
      .get(`${baseURL}/menu/get/DRAWER`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
      .then((result) => {
        return findFirstMenuRoute(result?.data?.data?.menu) || "";
      })
      .catch((error) => {
        console.error("Error fetching menu:", error);
        return "";
      });
  }

  const handleSubmit = () => {
    if (!otpValue || otpValue.trim() === "") {
      CustomAlert("error", "Please enter the OTP", {
        position: "top-center"
      });
      return;
    }

    setIsSubmitting(true);

    const payload = {
      "2FA-ID": String(twoFactorData?.["2FA-ID"] || ""),
      "2FA-Value": otpValue.trim()
    };

    axios
      .post(`${baseURL}/user/2FA/validate/${twoFactorData?.["2FA-Type"] || "OTP"}`, payload, {
        headers: {
          mode: "cors",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          "Content-Type": "application/json"
        }
      })
      .then((response) => {
        setToken(response?.data?.data?.access_token);
        setUserData(response?.data?.data);
        setAccessToken(response?.data?.data?.access_token);
        localStorage.setItem("userData", JSON.stringify(response?.data?.data));

        // Clear 2FA session data from sessionStorage on successful login
        sessionStorage.removeItem("twoFactorData");
        sessionStorage.removeItem("loginFormData");
        sessionStorage.removeItem("twoFactorAuthScreenIsOpen");
        sessionStorage.removeItem("twoFactorStartTime");

        toast.success("Login successful", {
          position: "top-center"
        });

        handleClose();

        fetchDrawerRoute(response?.data?.data?.access_token).then((route) => {
          navigate(route);
        });
      })
      .catch((error) => {
        let errorMessage = "OTP validation failed. Please try again.";
        
        if (error?.response) {
          const serverMsg = error?.response?.data?.metadata?.msg;
          if (serverMsg) {
            errorMessage = serverMsg;
          } else if (error.response.status === 401) {
            errorMessage = "Invalid OTP. Please try again.";
          } else if (error.response.status === 400) {
            errorMessage = "Invalid OTP format.";
          }
        }
        
        CustomAlert("error", errorMessage, {
          position: "top-center"
        });

        // Clear OTP input on error
        setOtpValue("");
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleResend = () => {
    setIsResending(true);

    const payload = {
      "2FA-ID": Number(twoFactorData?.["2FA-ID"]) || 0
    };

    axios
      .post(`${baseURL}/user/2FA/request/${twoFactorData?.["2FA-Type"] || "OTP"}`, payload, {
        headers: {
          mode: "cors",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET,PUT,POST,DELETE,PATCH,OPTIONS",
          "Content-Type": "application/json"
        }
      })
      .then((response) => {
        const statusCode = response?.data?.metadata?.statusCode;
        const message = response?.data?.metadata?.msg || "";
        
        if (statusCode === "2FAWAIT" || statusCode === "WAIT") {
          CustomAlert("error", message || "Please try again later", {
            position: "top-center"
          });
          return;
        }
        
        if (response?.data?.data && setTwoFactorData) {
          setTwoFactorData(response.data.data);
          // Update sessionStorage with new 2FA data
          sessionStorage.setItem("twoFactorData", JSON.stringify(response.data.data));
        }
        
        const newExpiry = response?.data?.data?.["2FA-Expiry"] || twoFactorData?.["2FA-Expiry"];
        if (newExpiry) {
          const expiryTime = getExpiryTimeInSeconds(newExpiry);
          setTimer(expiryTime);
          // Reset the start time for the new OTP
          sessionStorage.setItem("twoFactorStartTime", Date.now().toString());
        }

        toast.success("OTP has been resent successfully", {
          position: "top-center"
        });

        setOtpValue("");
      })
      .catch((error) => {
        let errorMessage = "Failed to resend OTP. Please try again.";
        
        if (error?.response) {
          const serverMsg = error?.response?.data?.metadata?.msg;
          if (serverMsg) {
            errorMessage = serverMsg;
          }
        }
        
        CustomAlert("error", errorMessage, {
          position: "top-center"
        });
      })
      .finally(() => {
        setIsResending(false);
      });
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !isSubmitting) {
      handleSubmit();
    }
  };

  const twoFactorType = twoFactorData?.["2FA-Type"];
  const inputMessage = twoFactorData?.["2FA-InputMessage"];

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const isResendDisabled = timer > 0 || isResending || isSubmitting;

  return (
    <>
      <BootstrapDialog
        aria-labelledby="customized-dialog-title"
        open={TwoFactorAuthScreenIsOpen}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          {twoFactorType}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500]
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <InputLabel htmlFor="otp-input">{inputMessage}:</InputLabel>
                <OutlinedInput
                  fullWidth
                  id="otp-input"
                  type="text"
                  value={otpValue}
                  onChange={(e) => setOtpValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  name="otp-input"
                  placeholder="Enter OTP"
                  inputProps={{
                    maxLength: 10,
                    inputMode: "numeric",
                    pattern: "[0-9]*"
                  }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
                {timer > 0 ? (
                  <>
                    OTP will expire in <strong>{formatTimer(timer)}</strong>
                  </>
                ) : (
                  null
                )}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button
                disableElevation
                disabled={isSubmitting}
                onClick={handleSubmit}
                fullWidth
                size="large"
                type="submit"
                variant="contained"
                color="primary"
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                disableElevation
                disabled={isResendDisabled}
                onClick={handleResend}
                fullWidth
                size="medium"
                variant="outlined"
                color="primary"
              >
                {isResending
                  ? "Resending..."
                  : timer > 0
                  ? `Resend ${twoFactorType} (${formatTimer(timer)})`
                  : `Resend ${twoFactorType}`}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
      </BootstrapDialog>
    </>
  );
}
